import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page } from 'react-pdf';

/**
 * Вёрстка, которую редактор прислал на утверждение (book_proofs,
 * backend/src/routes/proofs.ts). Встаёт в кабинете вместо блока «верстка».
 *
 * Развороты: 0 — обложка, дальше книга (как в блоке «верстка»: первый
 * разворот — пусто + стр. 1). На каждом развороте клиент может оставить
 * комментарий; внизу «Утвердить» (с подтверждением — книга уходит в печать)
 * или «Отправить правки», если комментарии есть.
 *
 * mode:
 *   review   — ждёт ответа клиента;
 *   changes  — правки отправлены, ждём новую версию (только просмотр);
 *   approved — книга утверждена, видна утверждённая вёрстка (только просмотр).
 *
 * Ссылка /forms#proof прокручивает кабинет прямо к этому блоку — её
 * отправляют клиенту вместе с вёрсткой.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

type Mode = 'review' | 'changes' | 'approved';
interface ProofInfo {
  id: number | null;
  pages: number;
  comments: { spread: number; text: string }[];
  hasBook: boolean;
  hasCover: boolean;
}

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const draftKey = (id: number) => `comabooks:proof-draft:${id}`;

const loadDraft = (id: number): Record<number, string> => {
  try {
    return JSON.parse(localStorage.getItem(draftKey(id)) || '{}');
  } catch {
    return {};
  }
};

const saveDraft = (id: number, draft: Record<number, string>) => {
  try {
    localStorage.setItem(draftKey(id), JSON.stringify(draft));
  } catch {
    /* приватный режим — черновик живёт только в памяти */
  }
};

interface Props {
  /** Сообщает Dashboard, есть ли что показать: тогда старый блок «верстка» прячется. */
  onActive: (active: boolean) => void;
  /** Клиент утвердил — Dashboard обновляет статус заказа. */
  onApproved?: () => void;
}

export const ProofReview: React.FC<Props> = ({ onActive, onApproved }) => {
  const { t } = useTranslation('dashboard');
  const [mode, setMode] = useState<Mode | null>(null);
  const [proof, setProof] = useState<ProofInfo | null>(null);
  const [bookUrl, setBookUrl] = useState<string | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [spread, setSpread] = useState(0);
  const [draft, setDraft] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [changesConfirmOpen, setChangesConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/proofs/my`, { headers: authHeaders() });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      setMode(data.mode ?? null);
      setProof(data.proof ?? null);
      onActive(!!data.mode);
      if (data.mode === 'review' && data.proof?.id) setDraft(loadDraft(data.proof.id));
    } catch {
      onActive(false);
    }
  }, [onActive]);

  useEffect(() => {
    load();
  }, [load]);

  // Файлы — через бэк (у CDN нет CORS), один раз на отправку.
  useEffect(() => {
    if (!mode || !proof) return;
    const urls: string[] = [];
    let cancelled = false;
    const get = async (kind: 'book' | 'cover', set: (u: string) => void) => {
      try {
        const res = await fetch(`${API_URL}/proofs/my/file/${kind}`, { headers: authHeaders() });
        if (!res.ok) return;
        const u = URL.createObjectURL(await res.blob());
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        urls.push(u);
        set(u);
      } catch {
        /* нет файла — разворот покажется пустым */
      }
    };
    if (proof.hasBook) get('book', setBookUrl);
    if (proof.hasCover) get('cover', setCoverUrl);
    return () => {
      cancelled = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [mode, proof]);

  const hasCover = !!proof?.hasCover;
  // Номер разворота в счёте клиента: 0 — обложка (если есть).
  const bookSpreads = numPages > 0 ? Math.ceil((numPages + 1) / 2) : 0;
  const total = (hasCover ? 1 : 0) + bookSpreads;
  const isCover = hasCover && spread === 0;
  const bookSpread = hasCover ? spread - 1 : spread;
  const left = bookSpread * 2;
  const right = bookSpread * 2 + 1;
  const leftVisible = !isCover && left >= 1 && left <= numPages;
  const rightVisible = !isCover && right >= 1 && right <= numPages;
  // Комментарий хранится по номеру разворота с обложкой = 0, даже если
  // обложки нет: так редактор всегда читает одинаково.
  const commentKey = hasCover ? spread : spread + 1;

  const readonlyComments = useMemo(() => {
    const m: Record<number, string> = {};
    for (const c of proof?.comments || []) m[c.spread] = c.text;
    return m;
  }, [proof]);

  const filled = Object.entries(draft).filter(([, v]) => v.trim());
  const editable = mode === 'review';

  const setComment = (text: string) => {
    if (!proof?.id) return;
    const next = { ...draft, [commentKey]: text };
    setDraft(next);
    saveDraft(proof.id, next);
  };

  const sendChanges = async () => {
    if (!proof?.id || !filled.length) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/proofs/my/${proof.id}/changes`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ comments: filled.map(([s, text]) => ({ spread: Number(s), text })) }),
      });
      if (!res.ok) throw new Error(String(res.status));
      try {
        localStorage.removeItem(draftKey(proof.id));
      } catch {
        /* ничего */
      }
      setDraft({});
      setChangesConfirmOpen(false);
      await load();
    } catch {
      setError(t('proof_error'));
      setChangesConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    if (!proof?.id) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/proofs/my/${proof.id}/approve`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(String(res.status));
      setConfirmOpen(false);
      await load();
      onApproved?.();
    } catch {
      setError(t('proof_error'));
      setConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  };

  // /forms#proof — прокрутить к блоку. Ещё раз после загрузки книги: лента
  // выше догружается и сдвигает блок вниз.
  const sectionRef = React.useRef<HTMLElement | null>(null);
  const scrolledFor = React.useRef<string>('');
  useEffect(() => {
    if (!mode || window.location.hash !== '#proof') return;
    const key = `${mode}:${numPages > 0}`;
    if (scrolledFor.current === key) return;
    scrolledFor.current = key;
    const timer = window.setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    return () => window.clearTimeout(timer);
  }, [mode, numPages]);

  if (!mode || !proof) return null;

  const indicator = isCover
    ? t('proof_cover')
    : leftVisible && rightVisible
      ? `${left}–${right}`
      : rightVisible
        ? `${right}`
        : leftVisible
          ? `${left}`
          : '—';

  const comment = editable ? draft[commentKey] || '' : readonlyComments[commentKey] || '';

  return (
    <section id="proof" ref={sectionRef} className="mg-section mg-dash__pages-section mg-proof">
      <div className="mg-label">{mode === 'approved' ? t('proof_approved_label') : t('proof_label')}</div>
      {mode === 'review' && <p className="mg-proof__hint">{t('proof_hint')}</p>}
      {mode === 'changes' && <p className="mg-proof__hint mg-proof__hint--accent">{t('proof_changes_sent')}</p>}

      <div className="mg-dash__preview">
        {/* Книжный Document монтируем всегда, чтобы знать число страниц и на обложке. */}
        <div className={isCover ? 'mg-proof__hidden' : undefined}>
          {bookUrl ? (
            <Document
              file={bookUrl}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              loading={<div className="mg-dash__preview-loading mg-meta">{t('pdf_loading')}</div>}
              className="mg-dash__preview-doc"
            >
              <div className="mg-dash__preview-spread">
                <div className="mg-dash__preview-page">
                  {leftVisible ? (
                    <Page
                      key={`proof-l-${left}`}
                      pageNumber={left}
                      width={380}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                      loading={<div className="mg-dash__preview-blank" />}
                    />
                  ) : (
                    <div className="mg-dash__preview-blank" />
                  )}
                </div>
                <div className="mg-dash__preview-page">
                  {rightVisible ? (
                    <Page
                      key={`proof-r-${right}`}
                      pageNumber={right}
                      width={380}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                      loading={<div className="mg-dash__preview-blank" />}
                    />
                  ) : (
                    <div className="mg-dash__preview-blank" />
                  )}
                </div>
              </div>
            </Document>
          ) : (
            <div className="mg-dash__preview-loading mg-meta">{t('pdf_loading')}</div>
          )}
        </div>

        {isCover && (
          <div className="mg-proof__cover">
            {coverUrl ? (
              <Document
                file={coverUrl}
                loading={<div className="mg-dash__preview-loading mg-meta">{t('pdf_loading')}</div>}
                className="mg-dash__preview-doc"
              >
                <Page pageNumber={1} width={760} renderAnnotationLayer={false} renderTextLayer={false} />
              </Document>
            ) : (
              <div className="mg-dash__preview-loading mg-meta">{t('pdf_loading')}</div>
            )}
          </div>
        )}

        <div className="mg-dash__preview-controls">
          <button
            type="button"
            className="mg-dash__preview-nav"
            onClick={() => spread > 0 && setSpread(spread - 1)}
            disabled={spread === 0}
            aria-label={t('preview_prev_aria') as string}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M15 6l-6 6 6 6" />
            </svg>
          </button>
          <span className="mg-dash__preview-indicator">
            {indicator}
            {numPages > 0 && !isCover && <span className="mg-dash__preview-indicator-total"> / {numPages}</span>}
          </span>
          <button
            type="button"
            className="mg-dash__preview-nav"
            onClick={() => spread < total - 1 && setSpread(spread + 1)}
            disabled={spread >= total - 1}
            aria-label={t('preview_next_aria') as string}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </div>

        {editable ? (
          <textarea
            className="mg-textarea mg-proof__comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('proof_comment_placeholder') as string}
            maxLength={2000}
            rows={3}
          />
        ) : (
          comment && (
            <div className="mg-proof__comment mg-proof__comment--read">
              <div className="mg-label mg-label--muted">{t('proof_your_comment')}</div>
              {comment}
            </div>
          )
        )}

        {editable && (
          <div className="mg-proof__actions">
            {filled.length > 0 && (
              <span className="mg-meta">{t('proof_comments_count', { n: filled.length })}</span>
            )}
            {error && <span className="mg-proof__error">{error}</span>}
            {filled.length > 0 ? (
              <button type="button" className="mg-btn mg-btn--ghost" onClick={() => setChangesConfirmOpen(true)} disabled={busy}>
                {t('proof_send_changes')}
              </button>
            ) : (
              <button type="button" className="mg-btn mg-btn--accent" onClick={() => setConfirmOpen(true)} disabled={busy}>
                {t('proof_approve')}
              </button>
            )}
          </div>
        )}
      </div>

      {changesConfirmOpen && (
        <div className="mg-popup-overlay" onClick={() => !busy && setChangesConfirmOpen(false)}>
          <div className="mg-popup" onClick={(e) => e.stopPropagation()}>
            <div className="mg-popup__title">{t('proof_changes_confirm_title')}</div>
            <p className="mg-popup__text">{t('proof_changes_confirm_text', { n: filled.length })}</p>
            <div className="mg-popup__actions">
              <button type="button" className="mg-btn mg-btn--accent" onClick={sendChanges} disabled={busy}>
                {t('proof_changes_confirm_yes')}
              </button>
              <button type="button" className="mg-btn mg-btn--ghost" onClick={() => setChangesConfirmOpen(false)} disabled={busy}>
                {t('proof_changes_confirm_no')}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmOpen && (
        <div className="mg-popup-overlay" onClick={() => !busy && setConfirmOpen(false)}>
          <div className="mg-popup" onClick={(e) => e.stopPropagation()}>
            <div className="mg-popup__title">{t('proof_confirm_title')}</div>
            <p className="mg-popup__text">{t('proof_confirm_text')}</p>
            <div className="mg-popup__actions">
              <button type="button" className="mg-btn mg-btn--accent" onClick={approve} disabled={busy}>
                {t('proof_confirm_yes')}
              </button>
              <button type="button" className="mg-btn mg-btn--ghost" onClick={() => setConfirmOpen(false)} disabled={busy}>
                {t('proof_confirm_no')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ProofReview;
